import { Component, EventEmitter, HostListener, Inject, Input, OnInit, Optional, Output } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { ProgressBarModule } from 'primeng/progressbar';
import { map, switchMap } from 'rxjs';
import { ButtonComponent } from "../../../@core/components/ui/button/button.component";
import { ToastService } from '../../../@core/services/toast.service';
import { RepositoryService } from '../../../contract-management/services/repository.service';
import { KNOWLEDGE_SCOPE } from '../../../contract-management/models/contract-management.model';
import { FileDropComponent } from '../file-drop/file-drop.component';
import { FileDropTypes } from '../file-drop/file-drop.types';

@Component({
  selector: 'app-upload-docs',
  standalone: true,
  imports: [ButtonComponent, ProgressBarModule, FileDropComponent, ButtonComponent],
  templateUrl: './upload-docs.component.html',
  styleUrl: './upload-docs.component.scss'
})
export class UploadDocsComponent implements OnInit {
  constructor(
    @Optional() private dailogRef: MatDialogRef<UploadDocsComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) private matDialogData: { origin?: string, type: string, customTitle?: string, customDescription?: string, knowledgeBaseStoreId?: string, project?: string | null, scope?: KNOWLEDGE_SCOPE },
    private repositoryService: RepositoryService,
    private toastService: ToastService,
    private router: Router
  ) { }

  /** When used inside `app-dialog` instead of MatDialog, pass config via this input. */
  @Input() dialogData?: { origin?: string, type: string, customTitle?: string, customDescription?: string, knowledgeBaseStoreId?: string, project?: string | null, scope?: KNOWLEDGE_SCOPE };
  @Output() dialogClosed = new EventEmitter<any>();

  get data(): { origin?: string, type: string, customTitle?: string, customDescription?: string, knowledgeBaseStoreId?: string, project?: string | null, scope?: KNOWLEDGE_SCOPE } {
    return this.dialogData ?? this.matDialogData ?? { type: 'file' };
  }

  @Input() title: string = '';
  @Input() showDesc: boolean = true;
  allowedFileTypes: string[] = [FileDropTypes.PDF, FileDropTypes.DOCX, FileDropTypes.DOC, FileDropTypes.XLSX, FileDropTypes.XLS]

  @Input() displayText: boolean = true;
  @Output() uploadStatus = new EventEmitter<boolean>();
  public type = ''
  dragAreaClass: string = '';
  fileName: string = '';
  isFileAdded: boolean = false;
  message: string = '';
  file!: File | File[];
  initiateUpload: boolean = false;

  ngOnInit(): void {
    this.type = this.data.type
    if (this.data?.origin && this.data?.type !== 'file') {
      this.allowedFileTypes = []
      this.allowedFileTypes.push(FileDropTypes.ZIP);
    }
  }

  onFileAddedChange(isAdded: boolean) {
    this.isFileAdded = isAdded;
    // Reset upload state when file is removed
    if (!isAdded) {
      this.initiateUpload = false;
      this.file = undefined as any;
    }
  }

  onFileRemove(fileToRemove: File) {
    if (Array.isArray(this.file)) {
      this.file = this.file.filter(f => f !== fileToRemove);
      this.isFileAdded = this.file.length > 0;
    } else {
      this.file = undefined as any;
      this.isFileAdded = false;
    }
  }

  getFilesArray(): File[] {
    if (!this.file) return [];
    return Array.isArray(this.file) ? this.file : [this.file];
  }

  @HostListener('dragover', ['$event'])
  onDragOver(event: DragEvent) {
    this.dragAreaClass = 'droparea';
    event.preventDefault();
  }

  @HostListener('dragenter', ['$event'])
  onDragEnter(event: DragEvent) {
    this.dragAreaClass = 'droparea';
    event.preventDefault();
  }

  @HostListener('dragend', ['$event'])
  onDragEnd(event: DragEvent) {
    this.dragAreaClass = 'dragarea';
    event.preventDefault();
  }

  @HostListener('dragleave', ['$event'])
  onDragLeave(event: DragEvent) {
    this.dragAreaClass = 'dragarea';
    event.preventDefault();
  }

  @HostListener('drop', ['$event'])
  onDrop(event: DragEvent) {
    this.dragAreaClass = 'dragarea';
    event.preventDefault();
    event.stopPropagation();

    if (event.dataTransfer?.files) {
      const files: FileList = event.dataTransfer.files;
      if (files.length > 0) {
        this.fileName = files[0].name;
        this.isFileAdded = true;

      }
    }
  }

  onFileChange(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      const files: FileList = input.files;
      this.file = files[0];
      this.fileName = files[0].name;
      this.isFileAdded = true;
      input.value = '';
    }
  }

  uploadFiles() {
    const files = Array.isArray(this.file) ? this.file : (this.file ? [this.file] : []);
    if (!files.length) {
      this.toastService.error('No files selected for upload');
      return;
    }

    this.initiateUpload = true;

    // Files go straight to S3, then we register the keys. Posting the bytes through
    // the API held every file in server memory and failed on large batches.
    this.repositoryService.uploadFilesToS3(files).pipe(
      switchMap(refs => this.repositoryService.registerUploads({
        files: refs,
        knowledgeBaseStoreId: this.data?.knowledgeBaseStoreId,
        project: this.data?.project ?? undefined,
        scope: this.data?.scope,
      }).pipe(map(res => ({ res, refs }))))
    ).subscribe({
      next: ({ res, refs }) => {
        const parentFolderId = this.repositoryService.getParentFolderId()
        if (res.success) {
          this.toastService.success(
            'File(s) have been successfully uploaded. The processing may take some time, please check back later',
            'Upload Complete'
          );
          // Processing is async now, so there are no saved file docs to hand back yet.
          // The ids are already known though, which is all the callers use.
          const uploaded = refs.map(ref => ({ _id: ref.fileId, name: ref.fileName }));
          this.dailogRef?.close(uploaded);
          this.dialogClosed.emit(uploaded);

          if (this.data?.origin === 'fromUploadDocumentButton') {
            if (parentFolderId) {
              this.router.navigate([`/settings/repository/${parentFolderId}`]);
            } else {
              this.router.navigate(['/settings/repository']);
            }
          }
          this.uploadStatus.emit(true);
        }
      },
      error: (err) => {
        this.toastService.error('Error uploading files: ' + err.message);
        this.initiateUpload = false;
      },
      complete: () => {
        this.initiateUpload = false;
      },
    });
  }

  onCloseDialog() {
    if (this.initiateUpload) {
      this.toastService.success('File will be uploaded in background');
    }
    this.dailogRef?.close();
    this.dialogClosed.emit(null);
  }
}

